import { SessionDetailsRoute } from '@routes/session-details/SessionDetailsRoute';
import { DirectionProvider } from '@radix-ui/react-direction';
import { Loader } from '@fe-learner-common/components/Loader';
import { useElementAttribute } from '@fe-learner-common/hooks/settings/useElementAttributes';
import { setupLuxon } from '@fe-learner-common/utils/date';
import { DetailsPageSkeleton } from '@components/DetailsPageSkeleton';
import { TW_ROOT } from '@utils/constants';
import { useApplyPageConfig } from '@routes/useApplyPageConfig';
import {
  NotFoundError,
  useAppContext,
  useBidiSupport,
  useConfigContext,
  useLanguages,
} from '@fe-core';
import { useParams } from 'react-router-dom';
import { useSessionDetails } from '@hooks/data/useSessionDetails';
import { useError } from '@hooks/useError';
import { publish } from '@fe-core-events';
import { IntlProvider, useIntl } from 'react-intl';

const TARGET_ELEMENT = `#${TW_ROOT}`;

export const SessionDetailsRouteLoader = () => {
  useApplyPageConfig();
  const intl = useIntl();
  const { config } = useConfigContext();
  const { sessionId } = useParams();
  const { isLoading: isLoadingAppContext } = useAppContext();
  const emitError = useError();

  const {
    data,
    error,
    isLoading: isLoadingSessionDetails,
  } = useSessionDetails({
    enabled: true,
    sessionId,
  });

  const courseLocale = data?.locale;

  const {
    isLoading: isLoadingLanguage,
    isError: isLanguageError,
    keys,
  } = useLanguages({
    locale: courseLocale,
    enabled: !!courseLocale,
    bundles: ['fe-learner'],
  });

  // no course locale, or its language failed to load: use the user's
  const hasCourseLocale = !!courseLocale && !isLanguageError;
  const locale = hasCourseLocale ? courseLocale : intl.locale;
  const messages = hasCourseLocale ? keys : intl.messages;

  setupLuxon(locale);
  useElementAttribute(
    {
      lang: locale,
    },
    TARGET_ELEMENT,
  );

  const { dir } = useBidiSupport({
    locale,
    elementToUpdate: TARGET_ELEMENT,
  });

  if (error) {
    if (error instanceof NotFoundError) {
      publish({
        eventName: 'feedback.ui.warning.base',
        data: {
          consumer: 'fe-learner-catalog',
          event: 'feedback.ui.warning.base',
          type: 'modal',
          status: 'warning',

          config: {
            title: intl.formatMessage({
              id: 'catalog.course.error.notfound.title',
              defaultMessage: 'Course not found',
            }),
            description: intl.formatMessage({
              id: 'catalog.course.error.notfound.description',
              defaultMessage: 'This course does not exist.',
            }),
          },
          action: {
            event: `${config.baseUrls.aleweb}catalog`,
            type: 'link' as const,
            text: {
              title: intl.formatMessage({
                id: 'learner.common.close',
                defaultMessage: 'Close',
              }),
            },
          },
        },
      });
      return;
    }

    emitError({
      error: error,
      errorMapOverride: {
        type: 'modal',
      },
    });
  }

  const isLoading =
    isLoadingAppContext || isLoadingSessionDetails || isLoadingLanguage;

  return (
    <Loader isLoading={isLoading} skeleton={<DetailsPageSkeleton />}>
      <IntlProvider
        fallbackOnEmptyString={true}
        locale={locale}
        messages={messages}
      >
        <DirectionProvider dir={dir}>
          <SessionDetailsRoute />
        </DirectionProvider>
      </IntlProvider>
    </Loader>
  );
};
